import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Money from './Money';
import InfoTip from './InfoTip';
import { GlossaryKey } from './glossary';

/**
 * Spec 059 — the glossary entry for promo spend is added to finance-glossary.json by the money
 * slice (byte-identical to the merchant-settlement copy, BR-31); the card only renders once the
 * overview API sends `flows.promotions`.
 */
const PROMOTIONS_KEY = 'promotions' as GlossaryKey;
import { FinanceOverviewResponse, OverviewFlows } from '../../types/overview';
import { cardLink } from './PositionCards';

interface FlowCardProps {
  title: string;
  glossaryKey: GlossaryKey;
  headline: ReactNode;
  to: string;
  linkLabel: string;
  testId: string;
  children?: ReactNode;
}

function FlowCard({ title, glossaryKey, headline, to, linkLabel, testId, children }: FlowCardProps) {
  return (
    <div className="admin-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }} data-testid={testId}>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
        {title}
        <InfoTip glossaryKey={glossaryKey} />
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{headline}</div>
      {children}
      <div style={{ marginTop: 'auto', paddingTop: '0.5rem', fontSize: '0.75rem' }}>
        <Link to={to}>{linkLabel} →</Link>
      </div>
    </div>
  );
}

function SubLine({ label, amount, glossaryKey, format }: { label: string; amount: number | null | undefined; glossaryKey: GlossaryKey; format?: 'aed' | 'count' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.8125rem' }}>
      <span style={{ color: 'var(--admin-text-muted)', display: 'inline-flex', alignItems: 'center' }}>
        {label}<InfoTip glossaryKey={glossaryKey} />
      </span>
      <Money amount={amount} glossaryKey={glossaryKey} format={format} hideTip />
    </div>
  );
}

interface FlowCardsProps {
  flows: OverviewFlows;
  links: FinanceOverviewResponse['links'];
}

/**
 * Spec 053 Flow C2 step 2 / AC-28b — flow cards for a period: sums of journals in the range
 * (Asia/Dubai `journal_date`), settlement counts from the subledger, Stripe payouts from billing.
 * `stripePayouts` is null when billing was unreachable — the card says so instead of showing 0.
 */
export default function FlowCards({ flows, links }: FlowCardsProps) {
  const providers = Object.entries(flows.collected.byProvider ?? {});
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(14rem, 100%), 1fr))', gap: '1rem' }} data-testid="flow-cards">
      <FlowCard title="Collected" glossaryKey="flowCollected" headline={<Money amount={flows.collected.total} glossaryKey="flowCollected" hideTip />}
        to={cardLink(links, 'collected')} linkLabel="Orders" testId="flow-collected">
        {providers.length > 1 && providers.map(([provider, amount]) => (
          <SubLine key={provider} label={provider} amount={amount} glossaryKey="flowCollected" />
        ))}
      </FlowCard>

      <FlowCard title="Paid to garages" glossaryKey="flowPaidToGarages" headline={<Money amount={flows.paidToGarages} glossaryKey="flowPaidToGarages" hideTip />}
        to={cardLink(links, 'paidToGarages')} linkLabel="Settlements" testId="flow-paidToGarages" />

      <FlowCard title="Refunded" glossaryKey="flowRefunded" headline={<Money amount={flows.refunded} glossaryKey="flowRefunded" hideTip />}
        to={cardLink(links, 'refunded')} linkLabel="Orders" testId="flow-refunded">
        {flows.goodwill !== 0 && <SubLine label="of which goodwill" amount={flows.goodwill} glossaryKey="flowGoodwill" />}
      </FlowCard>

      <FlowCard title="Stripe fees" glossaryKey="flowStripeFeeNet" headline={<Money amount={flows.stripeFees.net} glossaryKey="flowStripeFeeNet" hideTip />}
        to={cardLink(links, 'stripeFees')} linkLabel="Reports" testId="flow-stripeFees">
        <SubLine label="VAT on fees" amount={flows.stripeFees.vat} glossaryKey="flowStripeFeeVat" />
        {flows.stripeFees.recovered !== 0 && <SubLine label="Recovered from customers" amount={flows.stripeFees.recovered} glossaryKey="flowFeesRecovered" />}
      </FlowCard>

      <FlowCard title="Commission revenue" glossaryKey="flowCommissionRevenue" headline={<Money amount={flows.commission.revenue} glossaryKey="flowCommissionRevenue" hideTip />}
        to={cardLink(links, 'commission')} linkLabel="Reports" testId="flow-commission">
        <SubLine label="VAT" amount={flows.commission.vat} glossaryKey="flowCommissionVat" />
        {(flows.commission.creditNotesNet !== 0 || flows.commission.creditNotesVat !== 0) && (
          <>
            <SubLine label="Less credit notes (net)" amount={flows.commission.creditNotesNet} glossaryKey="flowCreditNotesNet" />
            <SubLine label="Less credit notes (VAT)" amount={flows.commission.creditNotesVat} glossaryKey="flowCreditNotesVat" />
          </>
        )}
      </FlowCard>

      <FlowCard title="Settlements" glossaryKey="settlementsTotalPaid" headline={<Money amount={flows.settlements.totalPaid} glossaryKey="settlementsTotalPaid" hideTip />}
        to={cardLink(links, 'settlements')} linkLabel="Settlements" testId="flow-settlements">
        <SubLine label="Created" amount={flows.settlements.created} glossaryKey="settlementsCreated" format="count" />
        <SubLine label="Approved" amount={flows.settlements.approved} glossaryKey="settlementsApproved" format="count" />
        <SubLine label="Paid" amount={flows.settlements.paid} glossaryKey="settlementsPaid" format="count" />
      </FlowCard>

      <FlowCard title="Stripe payouts" glossaryKey="payoutsTotal"
        headline={flows.stripePayouts
          ? <Money amount={flows.stripePayouts.total} glossaryKey="payoutsTotal" hideTip />
          : <span style={{ color: 'var(--admin-text-muted)', fontSize: '0.875rem', fontWeight: 500 }} data-testid="payouts-unavailable">Billing unavailable</span>}
        to={cardLink(links, 'stripePayouts')} linkLabel="Stripe payouts" testId="flow-stripePayouts">
        {flows.stripePayouts && <SubLine label="Payouts arrived" amount={flows.stripePayouts.count} glossaryKey="payoutsCount" format="count" />}
      </FlowCard>

      {/* Spec 068 V14 — a dashboard shows its zeros, so the card renders whenever the API sends it. */}
      {flows.serviceFees != null && (
        <FlowCard title="Service fees" glossaryKey="serviceFee"
          headline={<Money amount={flows.serviceFees.chargedGross} glossaryKey="serviceFee" hideTip />}
          to={cardLink(links, 'serviceFees')} linkLabel="Reports" testId="flow-serviceFees">
          <SubLine label="Charged (net)" amount={flows.serviceFees.chargedNet} glossaryKey="serviceFee" />
          <SubLine label="VAT" amount={flows.serviceFees.chargedVat} glossaryKey="serviceFee" />
          <SubLine label="Earned (net)" amount={flows.serviceFees.earnedNet} glossaryKey="serviceFee" />
          <SubLine label="Refunded to customers" amount={flows.serviceFees.refundedGross} glossaryKey="serviceFee" />
          <SubLine label="Recharged to garages" amount={flows.serviceFees.rechargedToGarages} glossaryKey="serviceFeeRecharge" />
          <SubLine label="Net kept" amount={flows.serviceFees.netKept} glossaryKey="serviceFee" />
        </FlowCard>
      )}

      {flows.promotions != null && (
        <FlowCard title="Promotions" glossaryKey={PROMOTIONS_KEY} headline={<Money amount={flows.promotions} glossaryKey={PROMOTIONS_KEY} hideTip />}
          to={cardLink(links, 'promotions')} linkLabel="Reports" testId="flow-promotions" />
      )}
    </div>
  );
}
